import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
    alpha,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    OutlinedInput,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { CollectionStatus, DeliveryMember, DeliveryMemberReversmentHistoryType, DeliveryReversmentIncludedCollectionType } from 'types';
import { checkNullable, getCollectionStatusChipStyle, parseCollectionStatus, separatePhoneBy2digits } from 'utils/utils';
import useStore from 'zustand-store';
import { format } from 'date-fns';
import { centsToDirhamsString } from 'utils';
import { ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table';
import ScrollX from 'components/ScrollX';
import { HeaderSort, TablePagination } from 'components/third-party/ReactTable';
import SearchIcon from '@mui/icons-material/Search';

interface ReactTableProps {
    columns: ColumnDef<DeliveryReversmentIncludedCollectionType, any>[];
    data: DeliveryReversmentIncludedCollectionType[];
    setGlobalPageNumber: (value: number) => void;
    setGlobalPageSize: (value: number) => void;
    globalPageNumber: number;
    globalPageSize: number;
    totalPages: number;
    globalFilterValue: string;
    setGlobalFilterValue: (value: string) => void;
    onGlobalSearchButtonClicked: () => void;
    appliedSearchValue: string;
}

function ReactTable({
    columns,
    data,
    setGlobalPageNumber,
    globalPageNumber,
    setGlobalPageSize,
    globalPageSize,
    totalPages,
    globalFilterValue,
    setGlobalFilterValue,
    onGlobalSearchButtonClicked,
    appliedSearchValue
}: ReactTableProps) {
    const theme = useTheme();
    const componentRef: React.Ref<HTMLDivElement> = useRef(null);
    const isFetching = useStore((state) => state.isFetching);

    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState({});

    const table = useReactTable({
        data,
        columns,
        pageCount: totalPages,
        state: {
            pagination: {
                pageIndex: globalPageNumber,
                pageSize: globalPageSize,
            },
            sorting,
            rowSelection,
        },
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        manualSorting: true,
        manualPagination: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const onGoToPage = (page: number) => {
        setGlobalPageNumber(page);
        table.setPageIndex(page);
    };

    const onSetPageSize = (pageSize: number) => {
        onGoToPage(0);
        setGlobalPageSize(pageSize);
        table.setPageSize(pageSize);
    };

    const rows = table.getPaginationRowModel().rows;

    const hasActiveFilters = appliedSearchValue !== "";

    const emptyMessage = hasActiveFilters
        ? "Aucun encaissement ne correspond à votre recherche."
        : "Aucun encaissement inclus dans ce reversement.";

    return (
        <Box>
            <Stack direction={'row'} spacing={2} justifyContent="space-between" alignItems="center" sx={{ p: 3, pb: 3 }}>
                <Stack direction={'row'} spacing={2} width={"50%"}>
                    <OutlinedInput
                        value={globalFilterValue}
                        onChange={(event) => { setGlobalFilterValue(event.target.value) }}
                        fullWidth
                        placeholder="Rechercher par REFERENCE FACTURE / BL ..."
                        startAdornment={<SearchIcon />}
                    />
                    <Button
                        disableElevation
                        disabled={isFetching}
                        size="medium"
                        type="submit"
                        variant="contained"
                        onClick={onGlobalSearchButtonClicked}
                    >
                        <SearchIcon />
                    </Button>
                </Stack>
            </Stack>

            <Box ref={componentRef}>
                <Table>
                    <TableHead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id} sx={{ '& > th:first-of-type': { width: '58px' } }}>
                                {headerGroup.headers.map(header => (
                                    <TableCell key={header.id} colSpan={header.colSpan} sx={{ width: header.column.getSize() }}>
                                        <HeaderSort column={header} sort />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            !isFetching && (
                                <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                                    <TableCell colSpan={columns.length} sx={{ py: 6 }}>
                                        <Stack alignItems="center" spacing={0.5}>
                                            <Typography color="textSecondary" variant="h5">
                                                {emptyMessage}
                                            </Typography>
                                            {hasActiveFilters && (
                                                <Typography color="textSecondary" variant="body2">
                                                    Vérifiez la référence facture / BL saisie et réessayez.
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            <>
                                {rows.map(row => (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: row.getIsSelected() ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit',
                                        }}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                                    <TableCell sx={{ p: 2, py: 3 }} colSpan={columns.length}>
                                        <TablePagination
                                            gotoPage={onGoToPage}
                                            setPageSize={onSetPageSize}
                                            pageSize={globalPageSize}
                                            pageIndex={globalPageNumber}
                                            totalPages={totalPages}
                                        />
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
}

const ReversmentDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const fetchReversmentsIncludedCollections = useStore(state => state.fetchReversmentsIncludedCollections);
    const [totalPages, setTotalPages] = useState<number>(5);
    const [globalPageNumber, setGlobalPageNumber] = useState(0);
    const [globalPageSize, setGlobalPageSize] = useState(5);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
    const [isGlobalSearchButtonClicked, setIsGlobalSearchButtonClicked] = useState<boolean>(false);
    const [appliedSearchValue, setAppliedSearchValue] = useState<string>("");

    const [includedReversments, setIncludedReversments] = useState<DeliveryReversmentIncludedCollectionType[]>([]);
    const [deliveryMemberDetails] = useState<DeliveryMember>(location.state?.deliveryMember);
    const [reversmentDetails] = useState<DeliveryMemberReversmentHistoryType>(location.state?.reversmentDetails);
    const professionalAccount = useStore(state => state.professionalAccount);

    type InfoRow = { label: string; value: string };

    const InfoCard = ({ title, rows }: { title: string; rows: InfoRow[] }) => (
        <Stack spacing={1.5}>
            <Typography variant="h5" fontWeight={700}>{title}</Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2 }}>
                {rows.map((row, index) => (
                    <Box key={row.label}>
                        {index > 0 && <Divider />}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                            <Typography color="textSecondary" sx={{ fontSize: 13 }}>{row.label}</Typography>
                            <Typography fontWeight={600}>{row.value}</Typography>
                        </Stack>
                    </Box>
                ))}
            </Box>
        </Stack>
    );

    const columns = useMemo<ColumnDef<DeliveryReversmentIncludedCollectionType>[]>(
        () => [
            {
                header: 'DATE',
                accessorKey: 'creationDate',
                enableSorting: false,
                cell: info => {
                    const value = info.getValue<Date>();
                    return <Typography whiteSpace={"pre"}>{format(new Date(value), "dd/MM/yyyy ' à ' HH:mm:ss")}</Typography>
                }
            },
            {
                header: 'MONTANT ENCAISSE',
                accessorKey: 'amount',
                enableSorting: false,
                cell: info => <Typography>{centsToDirhamsString(info.getValue<number>())} Dhs</Typography>
            },
            {
                header: 'REFERENCE FACTURE / BL',
                accessorKey: 'reference',
                enableSorting: false,
                cell: info => <Typography>{checkNullable(info.getValue<string>(), "N/A")}</Typography>
            },
            {
                header: 'STATUT',
                accessorKey: 'status',
                enableSorting: false,
                cell: info => {
                    const status = info.getValue<CollectionStatus>();
                    return (
                        <Chip
                            style={getCollectionStatusChipStyle(status)}
                            label={parseCollectionStatus(status)}
                            size="small"
                            variant="light"
                        />
                    );
                }
            },

        ],
        []
    );

    useEffect(() => {
        fetchReversmentsIncludedCollections({
            page: globalPageNumber,
            size: globalPageSize,
            regulationId: reversmentDetails.id,
            deliveryAgentUuid: deliveryMemberDetails.uuid,
            searchValue: appliedSearchValue,
        },
            (response) => {
                setIncludedReversments(response.reversementInvoices);
                setTotalPages(response.totalPages);
            });
    }, [globalPageNumber, globalPageSize, isGlobalSearchButtonClicked])

    if (location.state === null ||
        location.state === undefined ||
        location.state.deliveryMember === null ||
        location.state.deliveryMember === undefined ||
        location.state.reversmentDetails === null ||
        location.state.reversmentDetails === undefined
    ) {
        return <Navigate replace to={'/delivery'} />
    }

    return (
        <Box>
            <MainCard border>
                <Box sx={{
                    paddingTop: "16px",
                    paddingBottom: "16px",
                    display: "flex",
                    flexDirection: "row",
                    width: '100%',
                    alignItems: "center"
                }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            borderWidth: '2px',
                            borderColor: "#E0E0E3",
                            borderRadius: "4px",
                            backgroundColor: "#F8F8F9",
                            padding: "12px 15px",
                        }}
                    >
                        <ArrowBackIosNewIcon sx={{ color: "#373737", fontSize: "12px" }} />
                    </IconButton>
                    <Box sx={{ marginLeft: '25px' }}>
                        <Stack spacing={0} flexDirection="row" alignSelf="center" alignItems="center" justifyContent="left">
                            <Typography variant='h5' sx={{ mr: 1 }}>
                                Détail reversement
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </MainCard>
            <MainCard border title="Résumé" sx={{ marginTop: "24px" }}>
                <Stack direction="row" justifyContent="space-around" alignItems="center">
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Date du reversement</Typography>
                        <Typography whiteSpace={"pre"}>
                            {format(new Date(reversmentDetails.creationDate), "dd/MM/yyyy ' à ' HH:mm")}
                        </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Montant reversé</Typography>
                        <Typography>{centsToDirhamsString(reversmentDetails.totalAmount)} Dhs</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Encaissements inclus</Typography>
                        <Typography>{checkNullable(reversmentDetails.includedInvoicesCount, "N/A")}</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.5} alignItems="center">
                        <Typography color="textSecondary">Transaction ID</Typography>
                        <Typography>{checkNullable(reversmentDetails.transactionReference, "N/A")}</Typography>
                    </Stack>
                </Stack>
            </MainCard>

            <MainCard border sx={{ marginTop: "24px" }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InfoCard
                            title="Livreur"
                            rows={[
                                { label: "Prénom", value: checkNullable(deliveryMemberDetails.firstName, "N/A") },
                                { label: "Nom", value: checkNullable(deliveryMemberDetails.lastName, "N/A") },
                                { label: "Téléphone", value: deliveryMemberDetails.phoneNumber ? separatePhoneBy2digits(deliveryMemberDetails.phoneNumber) : "N/A" },
                            ]}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InfoCard title="Compte destinataire" rows={[
                            { label: "Entreprise", value: checkNullable(professionalAccount.companyName, "N/A") },
                            { label: "Téléphone", value: professionalAccount.walletPhoneNumber ? separatePhoneBy2digits(professionalAccount.walletPhoneNumber) : "N/A" },
                        ]} />
                    </Grid>
                </Grid>
            </MainCard>

            <MainCard border title="Encaissements inclus" sx={{ marginTop: "24px" }}>
                <ScrollX>
                    <ReactTable
                        columns={columns}
                        data={includedReversments}
                        appliedSearchValue={appliedSearchValue}
                        setGlobalPageNumber={setGlobalPageNumber}
                        setGlobalPageSize={setGlobalPageSize}
                        globalPageNumber={globalPageNumber}
                        globalPageSize={globalPageSize}
                        totalPages={totalPages}
                        globalFilterValue={globalFilterValue}
                        setGlobalFilterValue={setGlobalFilterValue}
                        onGlobalSearchButtonClicked={() => {
                            setAppliedSearchValue(globalFilterValue.trim());
                            setIsGlobalSearchButtonClicked(!isGlobalSearchButtonClicked);
                            setGlobalPageNumber(0);
                        }}
                    />
                </ScrollX>
            </MainCard>
        </Box>
    );
};

export default ReversmentDetails;